import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const listeners = useRef(new Map());

  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    // Auth via httpOnly cookie sent with the handshake
    return `${wsProtocol}://${wsHost}/ws/${user?.id}`;
  }, [user?.id, token]);

  const connect = useCallback(() => {
    if (!user?.id || !token || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());

      ws.onopen = () => {
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle auth response
          if (data.type === 'auth_success') {
            console.log('WebSocket authenticated');
            setIsConnected(true);
            return;
          }
          
          if (data.type === 'error') {
            console.error('WebSocket error:', data.message);
            setIsConnected(false);
            return;
          }
          
          setLastMessage(data);
          
          // Notify all listeners
          listeners.current.forEach((callback, type) => {
            if (type === data.type || type === 'all') {
              callback(data);
            }
          });
        } catch (e) {
          // Handle non-JSON messages (like pong)
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code);
        setIsConnected(false);
        
        // Don't reconnect if auth failed (4001 or 4003)
        if (event.code === 4001 || event.code === 4003) {
          console.log('WebSocket auth failed, not reconnecting');
          return;
        }
        
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isAuthenticated && token) {
            connect();
          }
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;

      // Keepalive ping
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, [user?.id, token, isAuthenticated, getWsUrl]);

  useEffect(() => {
    if (isAuthenticated && user?.id && token) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, user?.id, token, connect]);

  const subscribe = useCallback((type, callback) => {
    const id = `${type}_${Date.now()}`;
    listeners.current.set(id, callback);
    return () => {
      listeners.current.delete(id);
    };
  }, []);

  const value = {
    isConnected,
    lastMessage,
    subscribe,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
